import { useQuery } from 'react-query';
import { ChangeEvent, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { NextPageContext } from 'next';
import { FormElement } from '@pb/shared/ui/molecules/form-element';
import { TextInput } from '@pb/shared/ui/atoms/text-input';
import { Paragraph } from '@pb/shared/ui/atoms/paragraph';
import { Heading } from '@pb/shared/ui/atoms/heading';
import { Button } from '@pb/shared/ui/atoms/button';
import { getPropertyId } from '@pb/helpers';
import { Layout } from '@pb/apps/offers/ui/layout';
import { getFeatures } from '@pb/apps/offers/services/features';
import {
    EnvironmentContext,
    getAppEnvironmentSettings,
} from '@pb/apps/offers/services/environment';
import {
    initialBuyerAdditionalDetails,
    initialHomeserveOfferDetails,
    useOfferSession,
} from '@pb/apps/offers/services/buyer-offer-session';
import { getBuyerOfferDetails } from '@pb/apps/offers/services/buyer-offer-details';
import { formatAmount, removeCommas } from '@pb/apps/offers/helpers/formatters';
import { getSteps } from '@pb/apps/offers/components/molecules/stepper';
import { AmlInformation } from '@pb/apps/offers/components/molecules/aml-information';
import { LoadingSpinner } from '@pb/apps/offers/components/atoms/loading-spinner';
import { PropertyOfferOverview } from './property-offer-overview/property-offer-overview';
import {
    TMakeOfferProperties,
    TMakeOfferServerSideProps,
} from './[property-id].types';
import {
    StyledAddressContainer,
    StyledButtonContainer,
    StyledCalloutLabel,
    StyledFormElement,
    StyledFormElementContainer,
    StyledSecondaryCalloutMessage,
    StyledTextArea,
} from './[property-id].styled';

export default function MakeOffer({
    propertyId,
    cookies,
    environment,
}: TMakeOfferProperties) {
    const { projectHugoEnabled } = getFeatures();

    const router = useRouter();
    const offerSession = useOfferSession();
    const currentSessionState = offerSession.getOfferSession();

    if (currentSessionState && currentSessionState.propertyId !== propertyId) {
        offerSession.removeOfferSession();
    }

    const invalidOfferAmountMessage = 'A valid offer needs to be entered';

    const [offerAmountValidationMessage, setOfferAmountValidationMessage] =
        useState<string>('');

    const [hasPageLoaded, setHasPageLoaded] = useState<boolean>(false);

    const [offerAmount, setOfferAmount] = useState<string>(
        getInitialOfferAmount()
    );
    const [comment, setComment] = useState<string>(getInitialComment());

    const { data: buyerOfferDetails, error: hasGenericError } = useQuery(
        'buyer-offer-details',

        async () => await getBuyerOfferDetails(propertyId, cookies)
    );

    useEffect(() => {
        if (!hasPageLoaded && currentSessionState) {
            const sessionMainBuyer = offerSession.getMainBuyerDetails();
            if (
                currentSessionState.propertyId !== propertyId ||
                sessionMainBuyer?.id !==
                    buyerOfferDetails?.buyerOffer?.buyers[0].id
            ) {
                offerSession.removeOfferSession();
                router.push(`/sales/make-offer/${propertyId}`);
            }
        }

        setHasPageLoaded(true);
    }, [
        currentSessionState,
        hasPageLoaded,
        offerSession,
        router,
        buyerOfferDetails,
        propertyId,
    ]);

    function getInitialOfferAmount() {
        const currentSession = offerSession.getOfferSession();
        return currentSession ? currentSession.offerAmount.toString() : '';
    }

    function getInitialComment() {
        const currentSession = offerSession.getOfferSession();
        return currentSession ? currentSession.comment.toString() : '';
    }

    function getOffersInTheRegionOf(): number {
        if (buyerOfferDetails && buyerOfferDetails.buyerOffer) {
            return (
                buyerOfferDetails.buyerOffer.property.maxAdvertPrice ??
                buyerOfferDetails.buyerOffer.property.marketPrice ??
                0
            );
        }
        return 0;
    }

    function getAddress(): string {
        return buyerOfferDetails && buyerOfferDetails.buyerOffer
            ? buyerOfferDetails.buyerOffer.property.address
            : '';
    }

    function getImageUrl(): string {
        return buyerOfferDetails && buyerOfferDetails.buyerOffer
            ? buyerOfferDetails.buyerOffer.property.imageUrl
            : '';
    }

    function isFormValid(amount: string): boolean {
        const parsedAmount = Number(amount);
        const isOfferValid = !Number.isNaN(parsedAmount) && parsedAmount !== 0;
        isOfferValid
            ? setOfferAmountValidationMessage('')
            : setOfferAmountValidationMessage(invalidOfferAmountMessage);
        return isOfferValid;
    }

    function updateSessionStorage() {
        if (buyerOfferDetails && buyerOfferDetails.buyerOffer) {
            const isPurplePactApplicable = environment.purplePactFeatureFlag
                ? Boolean(
                      buyerOfferDetails?.buyerOffer?.property
                          ?.pbProtectApplicable
                  )
                : null;
            const currentSession = offerSession.getOfferSession();
            currentSession
                ? offerSession.storeOfferSession({
                      ...currentSession,
                      propertyId,
                      offerAmount: Number(offerAmount),
                      comment,
                      isPurplePactApplicable,
                  })
                : offerSession.storeOfferSession({
                      propertyId,
                      offerAmount: Number(offerAmount),
                      comment,
                      buyerAdditionalDetails: {
                          ...initialBuyerAdditionalDetails,
                          propertyId,
                      },
                      buyers: [],
                      homeserveOfferDetails: initialHomeserveOfferDetails,
                      isPurplePactApplicable,
                  });
        }
    }

    function handleContinueButton() {
        if (isFormValid(offerAmount)) {
            updateSessionStorage();
            router.push(`/sales/buyer-details/${propertyId}`);
        }
    }

    function handleOfferAmountChange(event: ChangeEvent<HTMLInputElement>) {
        const updatedOfferAmount = removeCommas(event.target.value);
        setOfferAmount(updatedOfferAmount);
        isFormValid(updatedOfferAmount);
    }

    function renderLoadingState() {
        return <LoadingSpinner />;
    }

    function renderLoadedState() {
        if (buyerOfferDetails && buyerOfferDetails.isDuplicate) {
            window.location.href = `${environment.baseUrl}/seller#!manageOffers`;
        }

        if (hasGenericError) {
            router.push('/sales/error');
            return renderLoadingState();
        }

        if (buyerOfferDetails && buyerOfferDetails.status === 401) {
            window.location.href = `${environment.baseUrl}/auth/log-in?returnUrl=/offers/sales/make-offer/${propertyId}`;
            return renderLoadingState();
        }

        const isPurplePactApplicableOverride =
            environment?.purplePactFeatureFlag &&
            buyerOfferDetails?.buyerOffer?.property?.pbProtectApplicable;

        return (
            <EnvironmentContext.Provider value={environment}>
                <Layout
                    steps={getSteps(
                        'Offer',
                        propertyId,
                        currentSessionState,
                        isPurplePactApplicableOverride
                    )}
                >
                    <Heading size={'small'} level={1} marginTop="none">
                        Make your offer
                    </Heading>
                    <StyledAddressContainer>
                        <Heading size="xxsmall" color="grey6">
                            {getAddress()}
                        </Heading>
                    </StyledAddressContainer>
                    <PropertyOfferOverview
                        offersInTheRegionOf={getOffersInTheRegionOf()}
                        photoUrl={getImageUrl()}
                    />
                    {offerAmountValidationMessage && (
                        <StyledSecondaryCalloutMessage
                            icon="alert"
                            status="error"
                        >
                            <StyledCalloutLabel>
                                Please ensure that all the required fields are
                                completed before proceeding.
                            </StyledCalloutLabel>
                        </StyledSecondaryCalloutMessage>
                    )}
                    <StyledFormElementContainer>
                        <StyledFormElement
                            id={'offerAmount'}
                            labelText={'What is your offer?'}
                            validationMessage={offerAmountValidationMessage}
                            validationState={
                                offerAmountValidationMessage.length > 0
                                    ? 'error'
                                    : 'success'
                            }
                            renderInput={properties => (
                                <TextInput
                                    {...properties}
                                    aria-label="offer-amount"
                                    width="max"
                                    value={formatAmount(offerAmount)}
                                    renderPrepended={() => '£'}
                                    onChange={handleOfferAmountChange}
                                    hasError={
                                        offerAmountValidationMessage.length > 0
                                    }
                                />
                            )}
                        />
                    </StyledFormElementContainer>

                    <FormElement
                        id={'moreInformation'}
                        labelText={'Why do you want to buy this property?'}
                        helperText={'(Optional)'}
                        renderInput={properties => (
                            <>
                                <Paragraph marginTop="none">
                                    This is your chance to tell the seller why
                                    you’re interested – it could help them with
                                    their decision.
                                </Paragraph>
                                <StyledTextArea
                                    {...properties}
                                    aria-label="more-information"
                                    value={comment}
                                    maxLength={1000}
                                    onChange={event => {
                                        setComment(event.target.value);
                                    }}
                                />
                            </>
                        )}
                    />
                    <StyledButtonContainer>
                        <Button onClick={handleContinueButton} width="max">
                            Continue
                        </Button>
                    </StyledButtonContainer>
                    {projectHugoEnabled ? <AmlInformation /> : null}
                </Layout>
            </EnvironmentContext.Provider>
        );
    }

    return (buyerOfferDetails || hasGenericError) && hasPageLoaded
        ? renderLoadedState()
        : renderLoadingState();
}

export async function getMakeOfferServerSideProps(
    context: NextPageContext
): Promise<TMakeOfferServerSideProps> {
    const cookies = context.req?.headers.cookie;
    const propertyId = getPropertyId(context.query);
    const environment = getAppEnvironmentSettings();

    return {
        props: {
            propertyId,
            cookies,
            environment,
        },
    };
}
